/**
 * CareAssignment — parent inbox index for therapist→parent artifacts.
 * Uses AppSync GraphQL (API key) so it works before amplify_outputs introspection is refreshed.
 */
import { v4 as uuidv4 } from 'uuid';
import { appsyncPublicGraphqlRequest, stripUndefined } from './appsyncPublicGraphql';
import { createNotification } from './notifications';
import { client } from './amplifyClient';

export type CareArtifactType = 'VIDEO' | 'SCREENER' | 'GOAL' | 'DOCUMENT';
export type CareAssignmentStatus = 'PENDING' | 'VIEWED' | 'COMPLETED' | 'CANCELLED';

export type CareAssignmentRow = {
  id: string;
  kidProfileId: string;
  parentUserId?: string | null;
  assignedByUserId: string;
  organizationId?: string | null;
  artifactType: CareArtifactType;
  title: string;
  status: CareAssignmentStatus;
  questionnaireAssignmentId?: string | null;
  kidAssignedVideoId?: string | null;
  milestoneTaskId?: string | null;
  documentKey?: string | null;
  documentUrl?: string | null;
  messageThreadId?: string | null;
  assignedAt?: string | null;
  completedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

const CARE_ASSIGNMENT_SELECTION = `
  id kidProfileId parentUserId assignedByUserId organizationId artifactType title status
  questionnaireAssignmentId kidAssignedVideoId milestoneTaskId documentKey documentUrl
  messageThreadId assignedAt completedAt createdAt updatedAt
`;

function mapCareAssignment(raw: Partial<CareAssignmentRow> | null | undefined): CareAssignmentRow | null {
  if (!raw?.id || !raw.kidProfileId || !raw.assignedByUserId || !raw.title || !raw.artifactType) {
    return null;
  }
  return {
    id: String(raw.id),
    kidProfileId: String(raw.kidProfileId),
    parentUserId: raw.parentUserId ?? null,
    assignedByUserId: String(raw.assignedByUserId),
    organizationId: raw.organizationId ?? null,
    artifactType: raw.artifactType as CareArtifactType,
    title: String(raw.title),
    status: (raw.status as CareAssignmentStatus) || 'PENDING',
    questionnaireAssignmentId: raw.questionnaireAssignmentId ?? null,
    kidAssignedVideoId: raw.kidAssignedVideoId ?? null,
    milestoneTaskId: raw.milestoneTaskId ?? null,
    documentKey: raw.documentKey ?? null,
    documentUrl: raw.documentUrl ?? null,
    messageThreadId: raw.messageThreadId ?? null,
    assignedAt: raw.assignedAt ?? null,
    completedAt: raw.completedAt ?? null,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
  };
}

function hasCareAssignmentModel(): boolean {
  return Boolean((client.models as any)?.CareAssignment);
}

export async function listCareAssignmentsForKid(kidProfileId: string): Promise<CareAssignmentRow[]> {
  if (hasCareAssignmentModel()) {
    try {
      const all: CareAssignmentRow[] = [];
      let nextToken: string | null | undefined = undefined;
      do {
        const res = await (client.models as any).CareAssignment.list({
          filter: { kidProfileId: { eq: kidProfileId } },
          limit: 100,
          nextToken: nextToken || undefined,
          authMode: 'apiKey',
        });
        for (const row of res.data ?? []) {
          const mapped = mapCareAssignment(row);
          if (mapped && mapped.status !== 'CANCELLED') all.push(mapped);
        }
        nextToken = res.nextToken ?? null;
      } while (nextToken);
      return all.sort((a, b) => String(b.assignedAt || b.createdAt || '').localeCompare(String(a.assignedAt || a.createdAt || '')));
    } catch {
      // fall through to GraphQL
    }
  }

  const all: CareAssignmentRow[] = [];
  let nextToken: string | null = null;
  do {
    const data = await appsyncPublicGraphqlRequest<{
      listCareAssignments?: {
        items?: Array<Partial<CareAssignmentRow> | null> | null;
        nextToken?: string | null;
      };
    }>(
      `query ListCareAssignments($filter: ModelCareAssignmentFilterInput, $limit: Int, $nextToken: String) {
        listCareAssignments(filter: $filter, limit: $limit, nextToken: $nextToken) {
          items { ${CARE_ASSIGNMENT_SELECTION} }
          nextToken
        }
      }`,
      {
        filter: { kidProfileId: { eq: kidProfileId } },
        limit: 200,
        nextToken,
      },
    );
    for (const item of data.listCareAssignments?.items || []) {
      const mapped = mapCareAssignment(item);
      if (mapped && mapped.status !== 'CANCELLED') all.push(mapped);
    }
    nextToken = data.listCareAssignments?.nextToken ?? null;
  } while (nextToken);
  return all.sort((a, b) =>
    String(b.assignedAt || b.createdAt || '').localeCompare(String(a.assignedAt || a.createdAt || '')),
  );
}

export type CreateCareAssignmentInput = {
  kidProfileId: string;
  assignedByUserId: string;
  title: string;
  artifactType: CareArtifactType;
  parentUserId?: string | null;
  organizationId?: string | null;
  questionnaireAssignmentId?: string | null;
  kidAssignedVideoId?: string | null;
  milestoneTaskId?: string | null;
  documentKey?: string | null;
  documentUrl?: string | null;
  notifyParentUserId?: string | null;
  actionUrl?: string | null;
};

export async function createCareAssignment(input: CreateCareAssignmentInput): Promise<CareAssignmentRow> {
  const now = new Date().toISOString();
  const id = uuidv4();
  const payload = stripUndefined({
    id,
    kidProfileId: input.kidProfileId,
    parentUserId: input.parentUserId ?? undefined,
    assignedByUserId: input.assignedByUserId,
    organizationId: input.organizationId ?? undefined,
    artifactType: input.artifactType,
    title: input.title,
    status: 'PENDING',
    questionnaireAssignmentId: input.questionnaireAssignmentId ?? undefined,
    kidAssignedVideoId: input.kidAssignedVideoId ?? undefined,
    milestoneTaskId: input.milestoneTaskId ?? undefined,
    documentKey: input.documentKey ?? undefined,
    documentUrl: input.documentUrl ?? undefined,
    assignedAt: now,
    createdAt: now,
    updatedAt: now,
  });

  let created: CareAssignmentRow | null = null;

  if (hasCareAssignmentModel()) {
    try {
      const res = await (client.models as any).CareAssignment.create(payload, { authMode: 'apiKey' });
      created = mapCareAssignment(res.data);
    } catch {
      created = null;
    }
  }

  if (!created) {
    const data = await appsyncPublicGraphqlRequest<{
      createCareAssignment?: Partial<CareAssignmentRow> | null;
    }>(
      `mutation CreateCareAssignment($input: CreateCareAssignmentInput!) {
        createCareAssignment(input: $input) { ${CARE_ASSIGNMENT_SELECTION} }
      }`,
      { input: payload },
    );
    created = mapCareAssignment(data.createCareAssignment);
  }

  if (!created) throw new Error('CareAssignment was not created. Deploy TinyWins schema with CareAssignment.');

  const toUserId = input.notifyParentUserId || input.parentUserId;
  if (toUserId) {
    const notifType =
      input.artifactType === 'GOAL'
        ? 'GOAL_CREATED'
        : input.artifactType === 'SCREENER'
          ? 'ASSESSMENT_ASSIGNED'
          : 'ASSESSMENT_ASSIGNED';
    try {
      await createNotification({
        fromUserId: input.assignedByUserId,
        toUserId,
        organizationId: input.organizationId ?? null,
        kidProfileId: input.kidProfileId,
        notificationType: notifType,
        title:
          input.artifactType === 'VIDEO'
            ? 'New video assigned'
            : input.artifactType === 'GOAL'
              ? 'New goal assigned'
              : input.artifactType === 'DOCUMENT'
                ? 'New document shared'
                : 'New screener assigned',
        body: input.title,
        payload: {
          careAssignmentId: created.id,
          artifactType: input.artifactType,
        },
        actionUrl:
          input.actionUrl ||
          `/assignments/${input.kidProfileId}?assignmentId=${created.id}`,
      });
    } catch (err) {
      console.warn('CareAssignment notification failed:', err);
    }
  }

  return created;
}

export async function markCareAssignmentStatus(
  id: string,
  status: CareAssignmentStatus,
): Promise<void> {
  const now = new Date().toISOString();
  const input = stripUndefined({
    id,
    status,
    updatedAt: now,
    ...(status === 'COMPLETED' ? { completedAt: now } : {}),
  });
  if (hasCareAssignmentModel()) {
    try {
      await (client.models as any).CareAssignment.update(input, { authMode: 'apiKey' });
      return;
    } catch {
      // fall through
    }
  }
  await appsyncPublicGraphqlRequest(
    `mutation UpdateCareAssignment($input: UpdateCareAssignmentInput!) {
      updateCareAssignment(input: $input) { id status }
    }`,
    { input },
  );
}

export type AssignVideoParams = {
  kidProfileId: string;
  assignedByUserId: string;
  assignedByCognitoSub?: string | null;
  organizationId?: string | null;
  parentUserId?: string | null;
  title: string;
  url?: string | null;
  therapixVideoId?: string | null;
  thumbnailUrl?: string | null;
};

export async function assignTherapixVideoToKid(params: AssignVideoParams): Promise<{
  videoId: string;
  careAssignment: CareAssignmentRow;
}> {
  const now = new Date().toISOString();
  const videoId = uuidv4();
  const videoPayload = stripUndefined({
    id: videoId,
    kidProfileId: params.kidProfileId,
    title: params.title,
    url: params.url ?? undefined,
    therapixVideoId: params.therapixVideoId ?? undefined,
    thumbnailUrl: params.thumbnailUrl ?? undefined,
    assignedByUserId: params.assignedByUserId,
    assignedByCognitoSub: params.assignedByCognitoSub ?? undefined,
    externalSource: 'MIRAPLUS',
    assignedAt: now,
  });

  let persistedVideoId = videoId;
  const hasVideoModel = Boolean((client.models as any)?.KidAssignedVideo);
  if (hasVideoModel) {
    try {
      const res = await (client.models as any).KidAssignedVideo.create(videoPayload, {
        authMode: 'apiKey',
      });
      if (res.data?.id) persistedVideoId = res.data.id;
    } catch (err) {
      console.warn('KidAssignedVideo model create failed; trying GraphQL', err);
      const data = await appsyncPublicGraphqlRequest<{
        createKidAssignedVideo?: { id?: string } | null;
      }>(
        `mutation CreateKidAssignedVideo($input: CreateKidAssignedVideoInput!) {
          createKidAssignedVideo(input: $input) { id }
        }`,
        { input: videoPayload },
      );
      if (data.createKidAssignedVideo?.id) persistedVideoId = data.createKidAssignedVideo.id;
    }
  } else {
    const data = await appsyncPublicGraphqlRequest<{
      createKidAssignedVideo?: { id?: string } | null;
    }>(
      `mutation CreateKidAssignedVideo($input: CreateKidAssignedVideoInput!) {
        createKidAssignedVideo(input: $input) { id }
      }`,
      { input: videoPayload },
    );
    if (data.createKidAssignedVideo?.id) persistedVideoId = data.createKidAssignedVideo.id;
  }

  const careAssignment = await createCareAssignment({
    kidProfileId: params.kidProfileId,
    assignedByUserId: params.assignedByUserId,
    parentUserId: params.parentUserId,
    organizationId: params.organizationId,
    artifactType: 'VIDEO',
    title: params.title,
    kidAssignedVideoId: persistedVideoId,
    notifyParentUserId: params.parentUserId,
    actionUrl: `/assignments/${params.kidProfileId}`,
  });

  return { videoId: persistedVideoId, careAssignment };
}

export function assignmentOpenPath(row: CareAssignmentRow): string {
  const kid = row.kidProfileId;
  switch (row.artifactType) {
    case 'SCREENER':
      return row.questionnaireAssignmentId
        ? `/questionnaire/${kid}?assignmentId=${row.questionnaireAssignmentId}`
        : `/assignments/${kid}`;
    case 'GOAL':
      return `/assignments/${kid}?assignmentId=${row.id}`;
    case 'VIDEO':
    case 'DOCUMENT':
      return `/assignments/${kid}?assignmentId=${row.id}`;
    default:
      return `/assignments/${kid}`;
  }
}

export function assignmentMessagePath(row: CareAssignmentRow): string {
  return `/messages/${row.kidProfileId}?assignmentId=${row.id}&artifact=${row.artifactType}`;
}
